import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useFizbo } from '@/hooks/useFizbo';

type FeatureCard = {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  bgColor: string;
  stat: string | number;
  statLabel: string;
  route?: string;
  badge?: string;
};

export default function Phase1Summary() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const { listings } = useFizbo();
  const [expanded, setExpanded] = useState(false);

  const avgScore = listings.length > 0 ? Math.round(
    listings.reduce((s, l) => s + (l.sellabilityScore ?? 0), 0) / listings.length
  ) : 0;
  const highScoreCount = listings.filter((l) => (l.sellabilityScore ?? 0) >= 80).length;
  const kapandiCount = listings.filter((l) => l.kanbanStage === 'Kapandı').length;
  const teklifCount = listings.filter((l) => l.kanbanStage === 'Teklif').length;

  const features: FeatureCard[] = [
    {
      id: 'skor',
      title: 'Satılabilirlik Skoru',
      description: 'Her ilanın satış potansiyelini 0-100 arası puanlar',
      icon: 'ri-bar-chart-box-line',
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      stat: avgScore,
      statLabel: 'Ort. Skor',
      badge: `${highScoreCount} ilan 80+`,
    },
    {
      id: 'rakip',
      title: 'Rakip Karşılaştırma',
      description: 'Bölgedeki rakip ilanlarla fiyat ve süre analizi',
      icon: 'ri-scales-3-line',
      color: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-50 dark:bg-amber-900/20',
      stat: 5,
      statLabel: 'Rakip İlan',
      badge: 'Piyasa Ortası',
    },
    {
      id: 'pdf',
      title: 'PDF Sunum',
      description: '3 şablonla profesyonel sunum PDF\'i oluştur',
      icon: 'ri-file-pdf-2-line',
      color: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-50 dark:bg-red-900/20',
      stat: 3,
      statLabel: 'Şablon',
      badge: 'jsPDF',
    },
    {
      id: 'kanban',
      title: 'Kanban Pano',
      description: 'Sürükle-bırak ile ilan aşama takibi',
      icon: 'ri-kanban-view',
      color: 'text-violet-600 dark:text-violet-400',
      bgColor: 'bg-violet-50 dark:bg-violet-900/20',
      stat: teklifCount + kapandiCount,
      statLabel: 'Teklif+Kapandı',
      route: '/fizbo-kanban',
      badge: `${kapandiCount} kapandı`,
    },
    {
      id: 'yasam',
      title: 'İlan Yaşam Döngüsü',
      description: 'Açılış, güncelleme ve kapanış zaman çizelgesi',
      icon: 'ri-timeline-view',
      color: 'text-sky-600 dark:text-sky-400',
      bgColor: 'bg-sky-50 dark:bg-sky-900/20',
      stat: 6,
      statLabel: 'Olay Tipi',
      badge: 'Timeline',
    },
    {
      id: 'harita',
      title: 'Harita & Lokasyon',
      description: 'Google Maps + POI puanlama (okul, hastane, ulaşım)',
      icon: 'ri-map-2-line',
      color: 'text-teal-600 dark:text-teal-400',
      bgColor: 'bg-teal-50 dark:bg-teal-900/20',
      stat: 8,
      statLabel: 'POI Noktası',
      badge: 'Lokasyon Skoru',
    },
    {
      id: 'musteri',
      title: 'Müşteri İlgisi Tahmini',
      description: 'Segment eşleştirme ve potansiyel alıcı analizi',
      icon: 'ri-user-heart-line',
      color: 'text-pink-600 dark:text-pink-400',
      bgColor: 'bg-pink-50 dark:bg-pink-900/20',
      stat: 4,
      statLabel: 'Segment',
      badge: '51 Eşleşme',
    },
    {
      id: 'davranis',
      title: 'Müşteri Davranış Analizi',
      description: 'Tıklama, görüntüleme ve ilgi dashboard\'u',
      icon: 'ri-line-chart-line',
      color: 'text-indigo-600 dark:text-indigo-400',
      bgColor: 'bg-indigo-50 dark:bg-indigo-900/20',
      stat: 136,
      statLabel: 'Haftalık Görüntüleme',
      badge: '53 tıklama',
    },
    {
      id: 'arama',
      title: 'Akıllı Arama Ön Bilgi',
      description: 'İlan no ile anlık piyasa özeti ve öneri',
      icon: 'ri-search-eye-line',
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
      stat: '%78',
      statLabel: 'Satış İhtimali',
      badge: 'AI Öneri',
    },
    {
      id: 'lokasyon',
      title: 'Lokasyon Avantaj Skoru',
      description: 'Çevre puanlama: ulaşım, eğitim, sağlık',
      icon: 'ri-map-pin-2-line',
      color: 'text-emerald-600 dark:text-emerald-400',
      bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
      stat: 91,
      statLabel: 'Maks. Lokasyon Skoru',
      badge: 'Atakum #1',
    },
  ];

  const visibleFeatures = expanded ? features : features.slice(0, 6);

  return (
    <div
      className={`rounded-xl border p-4 md:p-5 ${
        isDark ? 'bg-dark-card border-dark-border' : 'bg-white border-light-border'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-primary-500">
            <i className="ri-rocket-line text-white text-base" />
          </div>
          <div>
            <h2 className={`text-base font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              Aşama 1 Özellikleri
            </h2>
            <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
              10/10 özellik aktif
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
            Tümü Hazır ✓
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className={`w-full h-2 rounded-full ${isDark ? 'bg-dark-border' : 'bg-light-bg'}`}>
          <div className="h-full rounded-full bg-primary-500 w-full transition-all duration-1000" />
        </div>
        <div className="flex justify-between mt-1">
          <span className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>0</span>
          <span className="text-xs font-semibold text-primary-500">10 / 10 tamamlandı</span>
        </div>
      </div>

      {/* Feature Cards Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
        {visibleFeatures.map((f) => (
          <div
            key={f.id}
            onClick={() => f.route && navigate(f.route)}
            className={`rounded-xl p-3 transition-all ${f.bgColor} ${
              f.route ? 'cursor-pointer hover:scale-[1.02]' : ''
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="w-7 h-7 flex items-center justify-center">
                <i className={`${f.icon} text-base ${f.color}`} />
              </div>
              <span className={`w-4 h-4 flex items-center justify-center rounded-full bg-green-500`}>
                <i className="ri-check-line text-white text-xs" />
              </span>
            </div>
            <p className={`text-base font-bold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              {f.stat}
            </p>
            <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'} leading-tight`}>
              {f.statLabel}
            </p>
            <p className={`text-xs font-medium mt-1.5 truncate ${f.color}`}>
              {f.title}
            </p>
            {f.badge && (
              <span className={`text-xs mt-1 inline-block px-1.5 py-0.5 rounded-full ${
                isDark ? 'bg-dark-bg/60 text-dark-muted' : 'bg-white/70 text-light-muted'
              }`}>
                {f.badge}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Show More / Less */}
      <button
        onClick={() => setExpanded((p) => !p)}
        className={`mt-3 w-full text-xs font-medium py-2 rounded-lg transition-colors cursor-pointer ${
          isDark
            ? 'text-dark-muted hover:text-dark-text hover:bg-dark-border'
            : 'text-light-muted hover:text-light-text hover:bg-light-bg'
        }`}
      >
        {expanded ? (
          <span className="flex items-center justify-center gap-1">
            <i className="ri-arrow-up-s-line" /> Daha Az Göster
          </span>
        ) : (
          <span className="flex items-center justify-center gap-1">
            <i className="ri-arrow-down-s-line" /> Tüm 10 Özelliği Gör
          </span>
        )}
      </button>
    </div>
  );
}
